import { useCallback, useRef, useState } from 'react';
import { MediaAction, useHandleMediaDownload } from '@zonofi/chats-common';
import { useFileDownloader } from './download-file';
import {
  dispatch,
  MultiToastType,
  updateActionToastData,
} from '@zonofi/seller-rtk-store';
import { FileParams } from '@zonofi/common';

interface UseMediaHandlerOptions extends FileParams {
  onRender?: (url: string) => Promise<void> | void; // For images/audio rendering
  onPlay?: (url: string) => Promise<void> | void; // For audio autoplay
  maxRetries?: number;
}

export const useMediaHandler = ({
  filename = '',
  link,
  generateLinkParams,
  onRender,
  onPlay,
  maxRetries = 2,
}: UseMediaHandlerOptions) => {
  const retryCount = useRef(0);
  const { downloadFile, loading: fileLoading } = useFileDownloader();
  const [isError, setIsError] = useState(false);

  const onError = useCallback(() => {
    setIsError(true);
    dispatch(
      updateActionToastData({
        visibility: true,
        title: 'Error downloading media',
        description: 'Unable to load media at the moment, please try again.',
        type: MultiToastType.ERROR,
      })
    );
  }, []);

  const onSuccess = useCallback(
    async (url: string, action: MediaAction) => {
      try {
        switch (action) {
          case 'open':
            if (onPlay) {
              await onPlay(url);
            } else {
              window.open(url, '_blank');
            }
            break;

          case 'download':
            downloadFile(url, filename);
            break;

          case 'render':
            if (onRender) {
              await onRender(url);
            }
            break;

          default:
            console.warn(`Unhandled media action: ${action}`);
        }

        // ✅ Success: reset retry counter
        retryCount.current = 0;
        setIsError(false);
      } catch (err) {
        console.error(`Error in onSuccess (${action}):`, err);

        // ✅ Retry logic centralized here
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          const delay = 300 * retryCount.current; // exponential backoff
          console.warn(
            `Retrying ${action} (attempt ${retryCount.current}/${maxRetries}) in ${delay}ms...`
          );

          await new Promise((r) => setTimeout(r, delay));
          await handleMedia(action, true); // force S3 re-fetch
        } else {
          console.error(
            `Max retries (${maxRetries}) reached for ${action}. Aborting.`
          );
          onError();
        }
      }
    },
    [downloadFile, filename, onPlay, onRender, maxRetries, onError]
  );

  const { getMedia, isLoading } = useHandleMediaDownload({
    onSuccess,
    onError,
    filename,
    link,
  });

  const handleMedia = useCallback(
    async (action: MediaAction, forceS3?: boolean) => {
      try {
        await getMedia(generateLinkParams, action, forceS3);
      } catch {
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          await new Promise((r) => setTimeout(r, 300 * retryCount.current));
          return handleMedia(action, true);
        }
        onError();
      }
    },
    [getMedia, generateLinkParams, onError, maxRetries]
  );

  return {
    handleMedia,
    isLoading: fileLoading || isLoading,
    isError,
  };
};
